import { Link } from "react-router-dom";
import {
  Search,
  BarChart3,
  UserSearch,
  Network,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import { useAuth, goToLogin } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";

const REASONS = [
  {
    Icon: Search,
    title: "Find solutions to real AI problems",
    body: "Search for practical, tested solutions created by people who have already solved similar problems.",
  },
  {
    Icon: BarChart3,
    title: "Understand which AI models work best for specific tasks",
    body: "Compare how different models and tools have been used across real-world problems, including their strengths, limitations and outcomes.",
  },
  {
    Icon: UserSearch,
    title: "Find professionals with relevant technical experience",
    body: "Discover people based on the problems they have solved, the models they have used and the results they have produced.",
  },
  {
    Icon: Network,
    title: "Build a professional network around applied AI",
    body: "Connect with practitioners, collaborators, potential clients and specialists working on similar challenges.",
  },
  {
    Icon: ShieldCheck,
    title: "Build a credible reputation in AI",
    body: "Create a public record of completed work, technical approaches and measurable outcomes that demonstrates what you can actually deliver.",
  },
];

export default function AboutPage() {
  const { user } = useAuth();
  const { pathname } = useLocation();

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 lg:pt-24 pb-24">
      {/* Hero */}
      <section className="max-w-4xl">
        <h1
          data-testid="about-hero"
          className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#111827]"
        >
          A professional network for{" "}
          <span className="text-[#110A73]">real-world AI work</span>,
          where every contribution is proof of expertise.
        </h1>
        <p className="mt-8 text-lg sm:text-xl text-[#4B5563] leading-relaxed max-w-3xl">
          Oiiister is a professional network and knowledge base built around
          proven AI solutions — not opinions, not follower counts, not job
          titles. Search the problem. Find the fix. Meet the person who
          shipped it.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          {user ? (
            <Link
              to="/"
              data-testid="about-cta-primary"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold transition-colors"
            >
              Explore the directory
              <ArrowRight size={16} />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => goToLogin(pathname)}
              data-testid="about-cta-primary"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold transition-colors"
            >
              Create your profile
              <ArrowRight size={16} />
            </button>
          )}
          <Link
            to="/problems"
            data-testid="about-cta-secondary"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white ring-1 ring-[#E5E7EB] hover:ring-[#111827] text-[#111827] text-sm font-semibold transition-colors"
          >
            Ask a question
          </Link>
        </div>
      </section>

      {/* What the product is */}
      <section className="mt-24 lg:mt-32 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 lg:gap-16">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#110A73]">
            01 &middot; The product
          </p>
          <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight text-[#111827]">
            What Oiiister is.
          </h2>
        </div>
        <div className="space-y-6 text-base sm:text-lg text-[#4B5563] leading-relaxed max-w-3xl">
          <p>
            Oiiister is a professional network and searchable knowledge base
            built around real AI problems and proven solutions.
          </p>
          <p>
            Search for AI questions, workflows and business problems that other
            people have already solved. Each solution records the{" "}
            <span className="text-[#111827] font-medium">
              models, tools and methods used
            </span>
            , what was attempted, and the outcome achieved.
          </p>
          <p>
            Over time, this creates a structured database of applied AI
            knowledge: how different models perform, which tasks they are best
            suited to, where they fail, and which combinations of tools produce
            reliable results.
          </p>
          <p>
            Find an existing solution, contact the professional who created it,
            or post a new request when the right answer does not yet exist.
            Professionals document their work, demonstrate capability, and
            build a reputation based on{" "}
            <span className="text-[#111827] font-medium">
              verified problem-solving
            </span>{" "}
            — not job titles, follower counts or theoretical expertise.
          </p>
        </div>
      </section>

      {/* Why people would use it */}
      <section className="mt-24 lg:mt-32">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#110A73]">
            02 &middot; The value
          </p>
          <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight text-[#111827]">
            Why people use it.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {REASONS.map(({ Icon, title, body }, i) => (
            <article
              key={title}
              data-testid={`reason-${i}`}
              className="bg-white rounded-3xl p-7 ring-1 ring-[#F3F4F6] card-lift"
            >
              <span className="inline-flex w-11 h-11 rounded-2xl bg-[#D6FFEB] text-[#3FFFB6] items-center justify-center">
                <Icon size={20} strokeWidth={2.2} />
              </span>
              <h3 className="mt-5 font-display font-semibold text-lg text-[#111827] leading-snug">
                {title}
              </h3>
              <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">
                {body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Why it matters */}
      <section className="mt-24 lg:mt-32">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 lg:gap-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#110A73]">
              03 &middot; The reason
            </p>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight text-[#111827]">
              Why it matters.
            </h2>
          </div>
          <div className="space-y-6 text-base sm:text-lg text-[#4B5563] leading-relaxed max-w-3xl">
            <p>
              AI capabilities are changing rapidly, but most knowledge about how
              models perform is fragmented across social posts, private
              conversations, documentation and individual experimentation.
            </p>
            <p className="text-[#111827] font-medium">
              Oiiister turns that fragmented knowledge into a searchable,
              continuously expanding repository of applied AI intelligence.
            </p>
            <p>
              It helps people move beyond generic recommendations and identify
              which models, tools and practitioners are most effective for a
              specific problem.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mt-24 lg:mt-32">
        <div className="rounded-[2rem] bg-[#111827] text-white p-10 lg:p-16 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#3FFFB6] opacity-30 blur-3xl pointer-events-none" />
          <div className="relative max-w-2xl">
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Post your work.{" "}
              <span className="text-[#B8FFDD]">Get discovered.</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed">
              A profile on Oiiister is a record of what you can actually deliver
              with AI — searchable, contactable, permanent.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {user ? (
                <Link
                  to="/my/profile"
                  data-testid="about-final-cta"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-[#111827] text-sm font-semibold hover:bg-[#D6FFEB] hover:text-[#3FFFB6] transition-colors"
                >
                  Go to your profile
                  <ArrowRight size={16} />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => goToLogin(pathname)}
                  data-testid="about-final-cta"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-[#111827] text-sm font-semibold hover:bg-[#D6FFEB] hover:text-[#3FFFB6] transition-colors"
                >
                  Create your profile
                  <ArrowRight size={16} />
                </button>
              )}
              <Link
                to="/closed-problems"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/10 ring-1 ring-white/20 hover:bg-white/15 text-white text-sm font-semibold transition-colors"
              >
                Browse answered questions
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
